import { useCallback, useRef } from 'react';
import { Node, Edge } from 'reactflow';

interface UseParentHighlightingProps {
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
}

interface ParentInfo {
  nodeId: string;
  sourceHandle: string | null;
}

interface ChildInfo {
  nodeId: string;
  sourceHandle: string;
}

type HighlightState =
  | { type: 'none' }
  | { type: 'parent'; selectedNodeId: string }
  | { type: 'child'; sourceNodeId: string; sourceHandle: string };

interface UseParentHighlightingReturn {
  highlightParent: (selectedNodeId: string, currentEdges: Edge[]) => void;
  highlightChild: (
    sourceNodeId: string,
    sourceHandle: string,
    currentEdges: Edge[]
  ) => void;
  resetHighlighting: () => void;
  reapplyHighlighting: (currentEdges: Edge[]) => void;
  isHighlighted: React.MutableRefObject<boolean>;
}

function useParentHighlighting({
  setNodes,
  setEdges
}: UseParentHighlightingProps): UseParentHighlightingReturn {
  const isHighlightedRef = useRef(false);
  const highlightStateRef = useRef<HighlightState>({ type: 'none' });

  const findImmediateParent = (
    targetNodeId: string,
    edges: Edge[]
  ): ParentInfo | null => {
    const incomingEdge = edges.find((edge) => edge.target === targetNodeId);

    if (!incomingEdge) {
      return null;
    }

    return {
      nodeId: incomingEdge.source,
      sourceHandle: incomingEdge.sourceHandle ?? null
    };
  };

  const highlightParent = useCallback(
    (selectedNodeId: string, currentEdges: Edge[]) => {
      const parent = findImmediateParent(selectedNodeId, currentEdges);

      if (!parent) {
        return;
      }

      isHighlightedRef.current = true;
      highlightStateRef.current = { type: 'parent', selectedNodeId };

      setEdges((edges) =>
        edges.map((edge) => {
          const isHighlightedEdge =
            edge.target === selectedNodeId && edge.source === parent.nodeId;

          if (isHighlightedEdge) {
            return {
              ...edge,
              animated: true,
              style: {
                ...edge.style,
                stroke: '#3b82f6',
                strokeWidth: 2,
                opacity: 1
              },
              markerEnd:
                edge.markerEnd && typeof edge.markerEnd === 'object'
                  ? {
                      ...edge.markerEnd,
                      color: '#3b82f6'
                    }
                  : edge.markerEnd
            };
          }

          return {
            ...edge,
            style: {
              ...edge.style,
              opacity: 0.25
            }
          };
        })
      );

      setNodes((nodes) =>
        nodes.map((node) => {
          const isSelectedNode = node.id === selectedNodeId;
          const isParentNode = node.id === parent.nodeId;
          const shouldHighlight = isSelectedNode || isParentNode;

          return {
            ...node,
            style: {
              ...node.style,
              opacity: shouldHighlight ? 1 : 0.25
            },
            data: {
              ...node.data,
              highlightedHandle: isParentNode ? parent.sourceHandle : null
            }
          };
        })
      );
    },
    [setNodes, setEdges]
  );

  const findImmediateChild = (
    sourceNodeId: string,
    sourceHandle: string,
    edges: Edge[]
  ): ChildInfo | null => {
    const outgoingEdge = edges.find(
      (edge) =>
        edge.source === sourceNodeId && edge.sourceHandle === sourceHandle
    );

    if (!outgoingEdge) {
      return null;
    }

    return {
      nodeId: outgoingEdge.target,
      sourceHandle
    };
  };

  const highlightChild = useCallback(
    (sourceNodeId: string, sourceHandle: string, currentEdges: Edge[]) => {
      const child = findImmediateChild(
        sourceNodeId,
        sourceHandle,
        currentEdges
      );

      isHighlightedRef.current = true;
      highlightStateRef.current = { type: 'child', sourceNodeId, sourceHandle };

      setEdges((edges) =>
        edges.map((edge) => {
          const isHighlightedEdge =
            edge.source === sourceNodeId &&
            edge.sourceHandle === sourceHandle &&
            (child ? edge.target === child.nodeId : false);

          if (isHighlightedEdge) {
            return {
              ...edge,
              animated: true,
              style: {
                ...edge.style,
                stroke: '#3b82f6',
                strokeWidth: 2,
                opacity: 1
              },
              markerEnd:
                edge.markerEnd && typeof edge.markerEnd === 'object'
                  ? {
                      ...edge.markerEnd,
                      color: '#3b82f6'
                    }
                  : edge.markerEnd
            };
          }

          return {
            ...edge,
            style: {
              ...edge.style,
              opacity: 0.25
            }
          };
        })
      );

      setNodes((nodes) =>
        nodes.map((node) => {
          const isSourceNode = node.id === sourceNodeId;
          const isChildNode = child && node.id === child.nodeId;
          const shouldHighlight = isSourceNode || isChildNode;

          return {
            ...node,
            style: {
              ...node.style,
              opacity: shouldHighlight ? 1 : 0.25
            },
            data: {
              ...node.data,
              highlightedHandle: isSourceNode ? sourceHandle : null
            }
          };
        })
      );
    },
    [setNodes, setEdges]
  );

  const resetHighlighting = useCallback(() => {
    if (!isHighlightedRef.current) {
      return;
    }

    isHighlightedRef.current = false;
    highlightStateRef.current = { type: 'none' };

    setNodes((prevNodes) =>
      prevNodes.map((node) => ({
        ...node,
        style: {
          ...node.style,
          opacity: 1
        },
        data: {
          ...node.data,
          highlightedHandle: null
        }
      }))
    );

    setEdges((prevEdges) =>
      prevEdges.map((edge) => {
        const isFallbackEdge = edge.className?.includes('fallback');
        const originalStroke = isFallbackEdge ? 'rgb(5, 125, 153)' : '#b1b1b7';

        return {
          ...edge,
          animated: false,
          style: {
            ...edge.style,
            stroke: originalStroke,
            strokeWidth: 1.5,
            opacity: 1
          },
          markerEnd:
            edge.markerEnd && typeof edge.markerEnd === 'object'
              ? {
                  ...edge.markerEnd,
                  color: originalStroke
                }
              : edge.markerEnd
        };
      })
    );
  }, [setNodes, setEdges]);

  const reapplyHighlighting = useCallback(
    (currentEdges: Edge[]) => {
      if (!isHighlightedRef.current) {
        return;
      }

      const state = highlightStateRef.current;
      if (state.type === 'parent') {
        highlightParent(state.selectedNodeId, currentEdges);
      } else if (state.type === 'child') {
        highlightChild(state.sourceNodeId, state.sourceHandle, currentEdges);
      }
    },
    [highlightParent, highlightChild]
  );

  return {
    highlightParent,
    highlightChild,
    resetHighlighting,
    reapplyHighlighting,
    isHighlighted: isHighlightedRef
  };
}

export default useParentHighlighting;
